import { aggregateFromGradeCode, resolvePleDivision } from '../print/aggregateUtils';

export { aggregateFromGradeCode, resolvePleDivision };
export { computePleAggregateTotal } from '../print/aggregateUtils';

export const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};

export const readErr = (e, fallback = 'Operation failed') => {
  const errs = e?.response?.data?.errors;
  if (errs && typeof errs === 'object') {
    const first = Object.values(errs).flat().find(Boolean);
    if (first) return first;
  }
  return e?.response?.data?.message || fallback;
};

export const studentName = (s) =>
  s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || `#${s?.id}`;

export const acSx = { width: '100%', minWidth: { sm: 200 } };

export const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Published', label: 'Published' },
  { value: 'Printed', label: 'Printed' },
  { value: 'Sent', label: 'Sent' },
];

const assessmentTypeLabel = (assessmentType) =>
  assessmentType?.assessment_code ||
  assessmentType?.assessment_name ||
  assessmentType?.name ||
  (assessmentType?.id ? `Type #${assessmentType.id}` : null);

export const describeAssessmentScope = (assessmentConfigs = [], assessmentTypeIds = []) => {
  const selectedIds = Array.from(new Set((assessmentTypeIds || []).map(String).filter(Boolean)));
  if (!selectedIds.length) return 'All assessment types';

  const labelsById = new Map();
  assessmentConfigs.forEach((config) => {
    const assessmentType = config?.assessment_type;
    const id = assessmentType?.id;
    const label = assessmentTypeLabel(assessmentType);
    if (id != null && label && !labelsById.has(String(id))) {
      labelsById.set(String(id), label);
    }
  });

  const selectedLabels = selectedIds
    .map((id) => labelsById.get(String(id)))
    .filter(Boolean);

  return selectedLabels.length ? selectedLabels.join(', ') : 'Selected assessment types';
};

// Legacy — kept for PDF compat
export const marksLookup = (marksArr) => {
  const map = {};
  (marksArr || []).forEach((m) => { map[m.assessment_configuration_id] = m; });
  return map;
};
export const markDisplay = (m) => {
  if (!m) return '-';
  if (m.is_absent) return 'ABS';
  if (m.is_exempted) return 'EXM';
  const v = m.marks_obtained ?? m.raw_score ?? m.score;
  return v != null ? Number(v).toFixed(0) : '-';
};
export const gradeDisplay = (m) => {
  if (!m || m.is_absent || m.is_exempted) return '-';
  return m.assessment_grade || m.grade || '-';
};

/**
 * Extract unique assessment columns from report cards subjects_data.
 * Mirrors SubjectsTable assessmentCols logic (raw mode, include_in_final only).
 * Returns: [{ code, max_marks, weight }] sorted BOT → MOT → EOT
 */
export const extractAssessmentCols = (reportCards) => {
  const map = new Map();
  reportCards.forEach((rc) => {
    (rc.subjects_data || []).forEach((sub) => {
      (sub.papers || []).forEach((paper) => {
        (paper.assessments || []).forEach((a) => {
          const code = a.assessment_type?.assessment_code || a.assessment_code;
          if (!code) return;
          if (!map.has(code)) {
            map.set(code, { code, max_marks: a.max_marks, weight: a.weight_percentage });
          }
        });
      });
    });
  });
  const caKw = /^(bot|mot|ca|mid|cont|test|quiz|hw|home|assign|proj)/i;
  const eotKw = /^(eot|exam|end|final|term)/i;
  const order = (code) => { if (caKw.test(code)) return 0; if (eotKw.test(code)) return 2; return 1; };
  return Array.from(map.values()).sort((a, b) => order(a.code) - order(b.code));
};

/**
 * Extract unique subjects from report cards subjects_data.
 * Returns: [{ id, name, code }]
 */
export const extractSubjects = (reportCards) => {
  const map = {};
  reportCards.forEach((rc) => {
    (rc.subjects_data || []).forEach((sd) => {
      const subj = sd.subject;
      if (!subj?.id || map[subj.id]) return;
      map[subj.id] = {
        id: subj.id,
        name: subj.subject_name || subj.name || `Subject ${subj.id}`,
        code: subj.subject_code || subj.short_name || '',
      };
    });
  });
  return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Lookup grade+aggregate from boundaries for a given percentage.
 * boundaries: [{ min_percentage, max_percentage, grade, aggregate_value }]
 */
const gradeFromBoundaries = (pct, boundaries) => {
  if (!boundaries?.length) return { grade: null, aggregate: null };
  const sorted = [...boundaries].sort((a, b) => b.min_percentage - a.min_percentage);
  for (const b of sorted) {
    if (pct >= parseFloat(b.min_percentage) && pct <= parseFloat(b.max_percentage)) {
      return { grade: b.grade, aggregate: b.aggregate_value != null ? parseInt(b.aggregate_value, 10) : null };
    }
  }
  return { grade: null, aggregate: null };
};

/**
 * Build pivot from report cards (subjects_data, raw mode).
 *
 * 0 types selected  : overallPct from backend, no agg/div/position shown.
 * 1 type selected   : overallPct, aggregate, division and position all computed
 *                     from raw marks obtained for that assessment type only.
 * 2+ types selected : overallPct from raw marks, no agg/div/position shown.
 */
export const buildPivotData = (reportCards, _configs, subjectIds = null, gradingBoundaries = [], assessmentTypeIds = [], studentsList = []) => {
  // Build a quick lookup from the full students list for enriching missing rc.student
  const studentsMap = new Map(studentsList.map((s) => [String(s.id), s]));
  const allSubjects = extractSubjects(reportCards);
  const subjects = subjectIds?.length
    ? allSubjects.filter((s) => subjectIds.includes(String(s.id)))
    : allSubjects;

  const assessmentCols = extractAssessmentCols(reportCards);

  const isFiltered   = assessmentTypeIds.length > 0;
  const isSingleType = assessmentTypeIds.length === 1;

  const rows = reportCards.map((rc) => {
    const student = rc.student || { id: rc.student_id };

    const marksBySubject = {};
    (rc.subjects_data || []).forEach((sd) => {
      const subjId = sd.subject?.id;
      if (!subjId) return;
      marksBySubject[subjId] = {};
      (sd.papers || []).forEach((paper) => {
        (paper.assessments || []).forEach((a) => {
          const code = a.assessment_type?.assessment_code || a.assessment_code;
          if (code) marksBySubject[subjId][code] = a;
        });
      });
    });

    // Total raw marks
    let total = 0;
    subjects.forEach((subj) => {
      const byCode = marksBySubject[subj.id] || {};
      assessmentCols.forEach((col) => {
        const a = byCode[col.code];
        if (!a || a.is_absent || a.is_exempted) return;
        const raw = parseFloat(a.score ?? a.marks_obtained ?? a.raw_score ?? 0);
        if (!isNaN(raw)) total += raw;
      });
    });

    let aggregate = null, division = null, overallPct = 0, classPosition = null, classTotalStudents = null;

    if (!isFiltered) {
      // 0 types selected — use backend-stored values
      overallPct         = parseFloat(rc.overall_percentage ?? 0);
      classPosition      = rc.class_position;
      classTotalStudents = rc.class_total_students;
      // aggregate/division stay null (columns hidden when 0 types selected)

    } else if (isSingleType) {
      // Exactly 1 type — compute everything from raw marks for that assessment
      const bestCount = rc.subjects_counted ?? 4;
      let rawPctSum = 0, subjectCount = 0;

      const subjectAggs = subjects.map((subj) => {
        const byCode = marksBySubject[subj.id] || {};
        for (const col of assessmentCols) {
          const a = byCode[col.code];
          if (!a || a.is_absent || a.is_exempted) continue;

          // Raw percentage for this subject
          const maxMarks = parseFloat(a.max_marks ?? col.max_marks ?? 0);
          const rawMarks = parseFloat(a.score ?? a.marks_obtained ?? a.raw_score ?? 0);
          const rawPct   = maxMarks > 0 ? (rawMarks / maxMarks) * 100 : 0;
          rawPctSum  += rawPct;
          subjectCount++;

          // 1. assessment_grade code → aggregate
          const fromGrade = a.assessment_grade ? aggregateFromGradeCode(a.assessment_grade) : null;
          if (fromGrade != null) return fromGrade;
          // 2. raw percentage through boundaries
          const { aggregate: fromBoundary } = gradeFromBoundaries(rawPct, gradingBoundaries);
          if (fromBoundary != null) return fromBoundary;
        }
        return null;
      }).filter((v) => Number.isInteger(v) && v > 0).sort((a, b) => a - b);

      aggregate     = subjectAggs.length ? subjectAggs.slice(0, bestCount).reduce((s, v) => s + v, 0) : null;
      division      = resolvePleDivision(aggregate);
      // Use average raw % across subjects for position ranking
      overallPct    = subjectCount > 0 ? parseFloat((rawPctSum / subjectCount).toFixed(2)) : 0;
      classPosition = null; // re-ranked below
      classTotalStudents = rc.class_total_students;

    } else {
      // 2+ types — average raw % across all subjects × cols for sorting
      let rawPctSum = 0, subjectCount = 0;
      subjects.forEach((subj) => {
        const byCode = marksBySubject[subj.id] || {};
        assessmentCols.forEach((col) => {
          const a = byCode[col.code];
          if (!a || a.is_absent || a.is_exempted) return;
          const maxMarks = parseFloat(a.max_marks ?? col.max_marks ?? 0);
          const rawMarks = parseFloat(a.score ?? a.marks_obtained ?? a.raw_score ?? 0);
          if (maxMarks > 0) { rawPctSum += (rawMarks / maxMarks) * 100; subjectCount++; }
        });
      });
      overallPct = subjectCount > 0 ? parseFloat((rawPctSum / subjectCount).toFixed(2)) : 0;
    }

    // Per-assessment-col aggregate (best 4 subject grades for that col)
    const colAggregates = {};
    assessmentCols.forEach((col) => {
      const grades = subjects.map((subj) => {
        const a = (marksBySubject[subj.id] || {})[col.code];
        return a?.assessment_grade ?? null;
      }).filter(Boolean);
      const bestCount = rc.subjects_counted ?? 4;
      const aggs = grades.map(aggregateFromGradeCode).filter((v) => Number.isInteger(v) && v > 0).sort((a, b) => a - b);
      const aggTotal = aggs.length ? aggs.slice(0, bestCount).reduce((s, v) => s + v, 0) : null;
      colAggregates[col.code] = { aggTotal, division: resolvePleDivision(aggTotal) };
    });

    // Per-assessment-col position
    const colPositions = {};
    assessmentCols.forEach((col) => {
      for (const subj of subjects) {
        const a = (marksBySubject[subj.id] || {})[col.code];
        if (a?.assessment_rank != null) {
          colPositions[col.code] = { rank: a.assessment_rank, total: a.assessment_total_students ?? null };
          break;
        }
      }
    });

    return {
      student: { ...student, id: String(student.id) },
      marksBySubject,
      total,
      aggregate,
      division,
      overall_percentage: overallPct,
      class_position: classPosition,
      class_total_students: classTotalStudents,
      colAggregates,
      colPositions,
      isFiltered,
    };
  });

  // Re-rank positions when exactly 1 type selected
  if (isSingleType) {
    const sorted = [...rows].sort((a, b) => b.overall_percentage - a.overall_percentage);
    const total_students = rows.length;
    let lastPct = null, lastRank = 0;
    sorted.forEach((row, i) => {
      if (row.overall_percentage !== lastPct) { lastRank = i + 1; lastPct = row.overall_percentage; }
      row.class_position = lastRank;
      row.class_total_students = total_students;
    });
  }

  rows.sort((a, b) => {
    // Sort by performance (highest percentage first)
    if (b.overall_percentage !== a.overall_percentage) {
      return b.overall_percentage - a.overall_percentage;
    }
    // If same percentage, sort by total marks
    if (b.total !== a.total) {
      return b.total - a.total;
    }
    // If still tied, sort alphabetically by name
    return studentName(a.student).localeCompare(studentName(b.student));
  });
  return { subjects, rows, allSubjects, assessmentCols, isFiltered, isSingleType };
};
